import { ViewableDate, ViewableDateShort } from "../ViewableDate";

export {
  LockMessages,
  DeleteMessages,
  ReprocessMessages,
  SaveModelMessages,
  ChangeModelMessages,
  RenameModelMessages,
  BccMessages,
  ExportMessages,
  ImportMessages,
  CreateModelMessages,
  TableOffsetMessages,
  StaleMessages,
  LicenseMessages,
  LoginMessages,
  SpanTableMessages,
  MultipleAlertMessages,
  BatchClassReduxMessages,
  PageWarningMessages,
  LicenseExpiryMessages,
};

const DeleteMessages = {
  deleteManyBatch: (size: number) =>
    `Are you sure you want to delete ${size} batches?`,
  crud_deleteModel: () => "Are you sure you want to delete this template?",
  crud_deleteModelFailed: () =>
    "This template could not be deleted because a document was classified against it",
};
const ReprocessMessages = {
  unsavedChanges: () =>
    "There are templates with unsaved changes. If you continue these changes will be lost.\nPress Cancel to continue editing these templates",
  reprocessManyBatch: (size: number) =>
    `Are you sure you want to reprocess ${size} batches?`,
};

const SaveModelMessages = {
  noModelPages: (modelNames: string[]) => {
    if (modelNames.length === 0) return "";
    if (modelNames.length === 1)
      return `No template pages were saved for the template "${modelNames[0]}"`;
    return `No template pages were saved for \n"${modelNames.join('"\n')}"`;
  },
  errorSaving: (size: number) =>
    `An unknown error occured when saving these templates. Please try again`,
  crud_errorSaving: (modelName: string, batchClassId: string) =>
    `An error occurred saving ${modelName}:${batchClassId}.\n`,
};

const LockMessages = {
  failedToLock: (lockedObject: "Batch" | "Template") =>
    `An error occurred trying to lock this ${lockedObject}.`,
  config_objectNotLocked: (
    lockedObject: "Batch" | "Template",
    lockedBy?: string,
  ) =>
    `This ${lockedObject.toLowerCase()} is locked by ${lockedBy ?? "another user"}. Please select another batch to work on.`,
  config_adminUnlock: (lockedObject: "Batch" | "Template", lockedBy?: string) =>
    `This ${lockedObject.toLowerCase()} is locked by ${lockedBy ?? "another user"}. Would you like to unlock it?`,
  crud_objectNotLocked: (
    didUnlock: "unlocked" | "failed",
    didLock: boolean,
    modelName: string,
  ) => `${modelName} could not be ${didUnlock ? "locked" : "unlocked"}`,
  crud_templateAdminUnlock: (lockedBy: string | undefined, modelName: string) =>
    `${modelName} is locked by ${lockedBy ?? "another user"}. Would you like to unlock it?`,
  crud_bccAdminUnlock: (lockedBy: string | undefined, bcId: string) =>
    `The batch class ${bcId} is locked by ${lockedBy ?? "another user"}. Would you like to unlock it?`,
  crud_templateLockedBy: (lockedBy: string | undefined) =>
    `This template is locked by ${lockedBy ?? "another user"}. Please select another template to work on.`,
  crud_bccLockedBy: (lockedBy: string | undefined) =>
    `This batch class is locked by ${lockedBy ?? "another user"}. Please select another batch class.`,
  crud_failedToUnlock: (object: "Batch Class" | "Template", name: string) =>
    `The ${object} ${name} could not be unlocked.`,
};

const ChangeModelMessages = {
  crud_unsavedChanges: () =>
    `This template has unsaved changes and navigating away will discard those changes.`,
};

const RenameModelMessages = {
  overwriteExistingModel: () =>
    "A template with this name already exists.\n" +
    "Continuing will override the existing template.",
  emptyNameError: () => "Template cannot have an empty name.",
  nameAlreadyExists: () => "A template already exists with this name.",
};
const CreateModelMessages = {
  alreadyExists: (modelName: string, batchClassId: string) =>
    `A template with the name ${modelName} already exists in batch class ${batchClassId}`,
};

const BccMessages = {
  alreadyExists: () => "A Batch Class already exists with this ID",
  invalidBatchClassId: () =>
    "The Batch Class Id must start with 2 letters and contain only numbers and letters",
  errorSaving: () =>
    "An error occurred saving the batch class configuration.\n" +
    "Please refresh and try again.",
  errorDeleting: () =>
    "An error occurred deleting the batch class configuration.\n" +
    "Please refresh and try again.",
  errorSavingRoles: (batchClassId: string) =>
    `An unknown error occurred saving the role assignments for batch class ${batchClassId}\nPlease refresh the page and try again.`,
  deleteWarning: (batchClassId: string) =>
    `Warning, you are about to delete batch class ${batchClassId}.\n\n` +
    "This will delete all Template and Batch data for this Batch Class which cannot be recovered.\n" +
    "Please export any data you wish to keep before continuing.",
};

const ExportMessages = {
  beingPrepared: (
    object: "Batch Class" | "Templates" | "Template Pages" | "Batches",
  ) =>
    `The ${object.toLowerCase()} ${object === "Batch Class" ? "is" : "are"} being prepared for export.\nPlease wait for the files to download`,
  couldNotBeStarted: (
    object: "Batch Class" | "Template" | "Template Page" | "Batch",
  ) =>
    `The ${object.toLowerCase()} export could not be started. Please reload and try again`,
  couldNotBeDownloaded: (
    object: "Batch Class" | "Template" | "Template Page" | "Batch",
  ) =>
    `The ${object.toLowerCase()} export could not be downloaded. Please reload and try again`,
};

const ImportMessages = {
  errorBcc: (newBc: boolean) =>
    `Failed to ${newBc ? "create" : "update"} batch class from imported file.`,
  error: () => "Failed to import files. Please refresh and try agin.",
};

const TableOffsetMessages = {
  notCalculated: () =>
    "The table offset could not be calculated for this document. Please check the table start rule successfully matches a table row and try again.",
  crud_cantCalculate: () =>
    "The table offset can only be configured in the Accelerator section of the application.",
  crud_cantCalculateDisable: () =>
    "The table offset can only be configured in the Accelerator section of the application.\nTurn off table offset logic for this table?",
};

const StaleMessages = {
  staleBatch: () =>
    "This batch has been updated by another user since you loaded it.",
  staleBatchSaveTemplateChanges: () =>
    "Should template changes be saved before the batch is reloaded? New templates that have not been saved will be lost if changes are not saved.",
  staleModel: () =>
    "A newer version of this template has been saved, should this template be reloaded?",
};

const LicenseMessages = {
  tablesNotLicensed: () => "The Accelerator is not licensed to extract tables",
};

const LoginMessages = {
  serverRespondedWrong: () =>
    "The server did not respond correctly when logging in. Please contact your system administrator and try again",
  wrongCredentials: () =>
    "Please ensure the entered username and password are correct",
};

const SpanTableMessages = {
  errorLoadingSpans: () => "There was an error loading the text for page.",
  noSpansLoaded: (multiPage: boolean) =>
    `No data has been extracted from ${multiPage ? "these pages" : "this page"}`,
};

const MultipleAlertMessages = {
  classifiedMultiple: () =>
    "This document was originally classified by multiple templates",
  noTemplatesClassified: () => "No templates classified for this document.",
  errorCheckingMultiple: () =>
    "The system could not determine which templates classified against this document.",
};

const BatchClassReduxMessages = {
  exporting: {
    unsavedChanges: () =>
      `The Batch Class cannot be exported until all changes are saved`,
  },
  saving: {
    populateMandatory: (errors: string) =>
      "Please populate all mandatory fields before saving the Batch Class\n" +
      errors,
  },
  docTypeConfig: {
    noDocTypes: () => "This Batch Class has no Document Types",
  },
};

const PageWarningMessages = {
  reached: (max: number) =>
    `This system has exceeded the licensed page count of ${max} pages. Renew the Accelerator license to continue using the system`,
  pageCountClose: (used: number, max: number) =>
    `This system is near the licensed page count (${used}/${max} pages)`,
};

const LicenseExpiryMessages = {
  expired: (expiry: number) =>
    `This system's Accelerator License expired on ${ViewableDate(expiry)} pages. Renew the license to continue using the system`,
  expiresSoon: (expiry: number) =>
    `This system is approaching the license end date of ${ViewableDateShort(expiry)}. Renew the license before this date to ensure no service disruptions occur.`,
};
